/**
 * Validation utilities for form inputs
 */

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates an email address according to the acceptance criteria:
 * - Must contain exactly one "@" symbol
 * - Domain must contain at least one dot (e.g., gmail.com)
 * - No spaces in the domain
 * - Total length must not exceed 254 characters
 * - Local part (before @) must be 64 characters or less
 * - Domain TLD must be 2-63 characters
 */
export function validateEmail(email: string): ValidationResult {
  const trimmedEmail = email.trim();

  // Check if empty
  if (!trimmedEmail) {
    return { isValid: false, error: 'Email address is required' };
  }

  // Check total length (max 254 characters)
  if (trimmedEmail.length > 254) {
    return { isValid: false, error: 'Email address must not exceed 254 characters' };
  }

  // Check for exactly one "@" symbol
  const atCount = (trimmedEmail.match(/@/g) || []).length;
  if (atCount === 0) {
    return { isValid: false, error: 'Email address must contain an @ symbol' };
  }
  if (atCount > 1) {
    return { isValid: false, error: 'Email address must contain only one @ symbol' };
  }

  const [localPart, domainPart] = trimmedEmail.split('@');

  // Check local part length (max 64 characters)
  if (localPart.length > 64) {
    return { isValid: false, error: 'The part before @ must not exceed 64 characters' };
  }

  // Check local part is not empty
  if (localPart.length === 0) {
    return { isValid: false, error: 'Email address must have content before the @ symbol' };
  }

  // Check domain part is not empty
  if (!domainPart || domainPart.length === 0) {
    return { isValid: false, error: 'Email address must have a domain after the @ symbol' };
  }

  // Check for spaces in domain
  if (domainPart.includes(' ')) {
    return { isValid: false, error: 'Email domain cannot contain spaces' };
  }

  // Check domain contains at least one dot
  if (!domainPart.includes('.')) {
    return { isValid: false, error: 'Email domain must contain a dot (e.g., gmail.com)' };
  }

  // Extract TLD (part after the last dot)
  const lastDotIndex = domainPart.lastIndexOf('.');
  const tld = domainPart.substring(lastDotIndex + 1);

  // Validate TLD length (2-63 characters)
  if (tld.length < 2) {
    return { isValid: false, error: 'Email domain extension must be at least 2 characters' };
  }
  if (tld.length > 63) {
    return { isValid: false, error: 'Email domain extension must not exceed 63 characters' };
  }

  // Validate TLD contains only letters
  if (!/^[a-zA-Z]+$/.test(tld)) {
    return { isValid: false, error: 'Email domain extension must contain only letters' };
  }

  return { isValid: true };
}

/**
 * Normalizes a mobile number by:
 * - Stripping non-digits (spaces, hyphens, parentheses)
 * - Converting +61 prefix to 0
 */
export function normalizeMobileNumber(phone: string): string {
  // Strip non-digits except for leading +
  let normalized = phone.replace(/[^\d+]/g, '');

  // Handle +61 prefix - replace with 0
  if (normalized.startsWith('+61')) {
    normalized = '0' + normalized.substring(3);
  }

  // Remove any remaining non-digit characters
  normalized = normalized.replace(/\D/g, '');

  return normalized;
}

/**
 * Validates an Australian mobile phone number according to acceptance criteria:
 * - Strip non-digits (spaces, hyphens, parentheses)
 * - Handle +61 prefix by replacing with 0
 * - Must be exactly 10 digits
 * - Must start with 04 or 05
 */
export function validateMobileNumber(phone: string): ValidationResult {
  const trimmedPhone = phone.trim();

  // Check if empty
  if (!trimmedPhone) {
    return { isValid: false, error: 'Mobile number is required' };
  }

  // Normalize the number
  const normalized = normalizeMobileNumber(trimmedPhone);

  // Check length (must be exactly 10 digits)
  if (normalized.length !== 10) {
    return {
      isValid: false,
      error: 'Mobile number must be 10 digits (e.g., 0412 345 678)'
    };
  }

  // Check starts with 04 or 05
  if (!normalized.startsWith('04') && !normalized.startsWith('05')) {
    return {
      isValid: false,
      error: 'Mobile number must start with 04 or 05'
    };
  }

  return { isValid: true };
}

/**
 * Formats a normalized mobile number for display (e.g., 0412 345 678)
 */
export function formatMobileNumber(phone: string): string {
  const normalized = normalizeMobileNumber(phone);

  if (normalized.length !== 10) {
    return phone; // Return original if not valid length
  }

  // Format as 0412 345 678
  return `${normalized.slice(0, 4)} ${normalized.slice(4, 7)} ${normalized.slice(7)}`;
}

/**
 * Canonical form for comparing two mobile numbers (so formatting doesn't count as a change).
 * Uses normalizeMobileNumber so +61 / 61 international forms compare equal to their 04/05 form.
 */
export function mobileDigitsForComparison(phone: string): string {
  const normalized = normalizeMobileNumber(phone ?? '');
  if (normalized.length === 9 && (normalized.startsWith('4') || normalized.startsWith('5'))) {
    return '0' + normalized;
  }
  if (normalized.startsWith('61')) {
    return '0' + normalized.slice(2);
  }
  return normalized;
}

// --- Registration number (vehicle rego) ---

/** Min/max length for stored registration number (after stripping symbols). */
export const REGISTRATION_NUMBER_MIN_LENGTH = 2;
export const REGISTRATION_NUMBER_MAX_LENGTH = 9;

/**
 * Max length of raw registration field input when separators are allowed (spaces, hyphen, dot).
 * Worst case is one separator between each plate character (``2 * max - 1``).
 */
export const REGISTRATION_DISPLAY_INPUT_MAX_LENGTH =
  REGISTRATION_NUMBER_MAX_LENGTH * 2 - 1;

/**
 * Sanitize registration number for storage: uppercase, remove everything except A–Z and 0–9.
 * No hyphens, dots, or spaces are stored in the database.
 */
export function sanitizeRegistrationNumber(value: string): string {
  return (value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

/**
 * Filter user input for the registration field: allow only A–Z, 0–9, and symbols used for
 * readability (hyphen, dot, space). Converts to uppercase. Use in onChange so illegal
 * characters are never accepted.
 */
export function filterRegistrationInput(value: string): string {
  const upper = (value ?? '').toUpperCase();
  return upper.replace(/[^A-Z0-9\-.\s]/g, '');
}

/**
 * Apply {@link filterRegistrationInput}, then trim from the end until the sanitized
 * alphanumeric length is at most {@link REGISTRATION_NUMBER_MAX_LENGTH} and the raw
 * display length is at most {@link REGISTRATION_DISPLAY_INPUT_MAX_LENGTH}.
 * Use for controlled inputs so pasted or API-loaded values cannot exceed the limit.
 */
export function filterAndTruncateRegistrationInput(value: string): string {
  let v = filterRegistrationInput(value);
  while (
    v.length > 0 &&
    (sanitizeRegistrationNumber(v).length > REGISTRATION_NUMBER_MAX_LENGTH ||
      v.length > REGISTRATION_DISPLAY_INPUT_MAX_LENGTH)
  ) {
    v = v.slice(0, -1);
  }
  return v;
}

/**
 * Validate registration number (after optional display filtering).
 * Rules: length 2–9 (after sanitize), characters A–Z and 0–9 only.
 * Returns ValidationResult with error message for display.
 */
export function validateRegistrationNumber(value: string): ValidationResult {
  const sanitized = sanitizeRegistrationNumber(value);

  if (sanitized.length === 0) {
    return { isValid: false, error: 'Registration number is required' };
  }

  if (sanitized.length < REGISTRATION_NUMBER_MIN_LENGTH) {
    return {
      isValid: false,
      error: `Registration number must be at least ${REGISTRATION_NUMBER_MIN_LENGTH} characters`,
    };
  }

  if (sanitized.length > REGISTRATION_NUMBER_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Registration number must not exceed ${REGISTRATION_NUMBER_MAX_LENGTH} characters`,
    };
  }

  if (!/^[A-Z0-9]+$/.test(sanitized)) {
    return {
      isValid: false,
      error: 'Registration number can only contain letters (A–Z) and numbers (0–9)',
    };
  }

  return { isValid: true };
}

/**
 * Validates registration when the field is optional (empty is valid).
 * Non-empty values use the same length and character rules as {@link validateRegistrationNumber}.
 */
export function validateOptionalRegistrationNumber(value: string): ValidationResult {
  const display = (value ?? '').trim();
  if (!display) {
    return { isValid: true };
  }
  const sanitized = sanitizeRegistrationNumber(display);
  if (!sanitized) {
    return {
      isValid: false,
      error: 'Registration number can only contain letters (A–Z) and numbers (0–9)',
    };
  }
  if (sanitized.length < REGISTRATION_NUMBER_MIN_LENGTH) {
    return {
      isValid: false,
      error: `Registration number must be at least ${REGISTRATION_NUMBER_MIN_LENGTH} characters`,
    };
  }
  if (sanitized.length > REGISTRATION_NUMBER_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Registration number must not exceed ${REGISTRATION_NUMBER_MAX_LENGTH} characters`,
    };
  }
  if (!/^[A-Z0-9]+$/.test(sanitized)) {
    return {
      isValid: false,
      error: 'Registration number can only contain letters (A–Z) and numbers (0–9)',
    };
  }
  return { isValid: true };
}

// --- Australian postcode / state validation ---

export const AU_POSTCODE_RANGES: ReadonlyArray<{ readonly state: string; readonly min: number; readonly max: number }> = [
  { state: 'NT',  min: 800,  max: 999 },
  { state: 'NSW', min: 1000, max: 1999 },
  { state: 'NSW', min: 2000, max: 2599 },
  { state: 'ACT', min: 2600, max: 2618 },
  { state: 'NSW', min: 2619, max: 2899 },
  { state: 'ACT', min: 2900, max: 2920 },
  { state: 'NSW', min: 2921, max: 2999 },
  { state: 'VIC', min: 3000, max: 3999 },
  { state: 'QLD', min: 4000, max: 4999 },
  { state: 'SA',  min: 5000, max: 5999 },
  { state: 'WA',  min: 6000, max: 6999 },
  { state: 'TAS', min: 7000, max: 7999 },
  { state: 'VIC', min: 8000, max: 8999 },
  { state: 'QLD', min: 9000, max: 9999 },
]

export const CROSS_BORDER_POSTCODES: Readonly<Record<string, ReadonlyArray<string>>> = {
  '0872': ['NT', 'SA', 'WA'],
  '3691': ['VIC', 'NSW'],
  '3707': ['VIC', 'NSW'],
  '2406': ['NSW', 'QLD'],
  '4380': ['QLD', 'NSW'],
  '4383': ['QLD', 'NSW'],
}

/** True if `postcode` is exactly 4 numeric digits. */
export function validateAuPostcodeFormat(postcode: string): boolean {
  return /^\d{4}$/.test(postcode.trim())
}

/**
 * True if `postcode` is a recognised match for `state`.
 * Applies the cross-border lookup before standard range checks.
 * Returns `true` when either argument is empty — defers to required-field checks.
 */
export function validatePostcodeStateMatch(postcode: string, state: string): boolean {
  const pc = postcode.trim()
  const st = state.trim()
  if (!pc || !st) return true
  const crossBorderAllowed = CROSS_BORDER_POSTCODES[pc]
  if (crossBorderAllowed) return crossBorderAllowed.includes(st)
  const num = Number(pc)
  return AU_POSTCODE_RANGES.some((r) => r.state === st && num >= r.min && num <= r.max)
}

// --- Registration number (strict update flow: dedicated page, alphanumeric-only field) ---

/**
 * Filter input for the registration update page: allow only A–Z and 0–9 (case-insensitive);
 * stores uppercase. Use in onChange so invalid characters are not retained.
 */
export function filterStrictAlphanumericRegistrationInput(value: string): string {
  return (value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

/**
 * Validate registration for the dedicated update page. Rules: required; length 2–9 after trim;
 * characters alphanumeric only (A–Z, a–z, 0–9). Does not modify existing validateRegistrationNumber behaviour.
 */
export function validateRegistrationNumberStrict(value: string): ValidationResult {
  const trimmed = (value ?? '').trim();

  if (trimmed.length === 0) {
    return { isValid: false, error: 'Registration number is required.' };
  }

  if (!/^[A-Za-z0-9]+$/.test(trimmed)) {
    return { isValid: false, error: 'Registration number must be alphanumeric only.' };
  }

  const sanitized = sanitizeRegistrationNumber(trimmed);

  if (sanitized.length < REGISTRATION_NUMBER_MIN_LENGTH) {
    return { isValid: false, error: 'Registration number must be at least 2 characters.' };
  }

  if (sanitized.length > REGISTRATION_NUMBER_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Registration number must be ${REGISTRATION_NUMBER_MAX_LENGTH} characters or fewer.`,
    };
  }

  return { isValid: true };
}
